import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useAuthStore } from '../stores/authStore';
import { 
  Trophy, 
  Medal, 
  Crown, 
  Flame, 
  Zap,
  TrendingUp,
  Award
} from 'lucide-react';
import { 
  FadeIn, 
  StaggerContainer, 
  StaggerItem,
  AnimatedCard,
  LoadingSpinner 
} from '../components/ui/AnimatedCard';

export function Leaderboard() {
  const [period, setPeriod] = useState('all');
  const { data: leaderboard, isLoading } = useLeaderboard({ period, limit: 50 });
  const { user } = useAuthStore();

  const periodOptions = [
    { value: 'weekly', label: 'This Week' },
    { value: 'monthly', label: 'This Month' },
    { value: 'all', label: 'All Time' },
  ];

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-amber-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-700" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-slate-400">{rank}</span>;
  };

  const getRankStyle = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200';
    if (rank === 2) return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200';
    return 'bg-white border-slate-200';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="text-center mb-8">
          <motion.div
            initial={{ rotate: -10 }}
            animate={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl mb-4"
          >
            <Trophy className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-slate-900">Leaderboard</h1>
          <p className="text-slate-500 mt-1">See who's leading the pack</p>
        </div>
      </FadeIn>

      {/* Period Filter */}
      <FadeIn delay={0.1}>
        <div className="flex justify-center gap-2">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPeriod(option.value)}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                period === option.value
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </FadeIn>

      {/* Top 3 Podium */}
      {!isLoading && leaderboard && leaderboard.length >= 3 && (
        <FadeIn delay={0.2}>
          <div className="flex items-end justify-center gap-4 py-8">
            {/* 2nd Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full flex items-center justify-center text-white font-bold text-xl mb-2">
                {leaderboard[1].name.charAt(0)}
              </div>
              <div className="text-sm font-semibold text-slate-700">{leaderboard[1].name}</div>
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <Zap className="w-3 h-3" />
                {leaderboard[1].points}
              </div>
              <div className="w-20 h-24 bg-gradient-to-t from-slate-200 to-slate-300 rounded-t-xl mt-2 flex items-center justify-center">
                <span className="text-2xl font-bold text-slate-600">2</span>
              </div>
            </motion.div>

            {/* 1st Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center"
            >
              <Crown className="w-8 h-8 text-amber-500 mb-1" />
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-2 ring-4 ring-amber-200">
                {leaderboard[0].name.charAt(0)}
              </div>
              <div className="text-base font-bold text-slate-900">{leaderboard[0].name}</div>
              <div className="flex items-center gap-1 text-sm text-amber-600 font-semibold">
                <Zap className="w-4 h-4" />
                {leaderboard[0].points}
              </div>
              <div className="w-24 h-32 bg-gradient-to-t from-amber-300 to-amber-400 rounded-t-xl mt-2 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
            </motion.div>

            {/* 3rd Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-700 rounded-full flex items-center justify-center text-white font-bold text-xl mb-2">
                {leaderboard[2].name.charAt(0)}
              </div>
              <div className="text-sm font-semibold text-slate-700">{leaderboard[2].name}</div>
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <Zap className="w-3 h-3" />
                {leaderboard[2].points}
              </div>
              <div className="w-20 h-16 bg-gradient-to-t from-amber-200 to-amber-300 rounded-t-xl mt-2 flex items-center justify-center">
                <span className="text-2xl font-bold text-amber-800">3</span>
              </div>
            </motion.div>
          </div>
        </FadeIn>
      )}

      {/* Leaderboard List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : (
        <StaggerContainer className="space-y-3">
          {leaderboard?.map((entry: any, index: number) => {
            const isCurrentUser = user?.id === entry.id;
            return (
              <StaggerItem key={entry.id}>
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    getRankStyle(entry.rank)
                  } ${isCurrentUser ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}
                >
                  {/* Rank */}
                  <div className="flex-shrink-0 w-10">
                    {getRankIcon(entry.rank)}
                  </div>

                  {/* Avatar */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                    entry.rank <= 3 
                      ? 'bg-gradient-to-br from-primary-500 to-secondary-500' 
                      : 'bg-slate-400'
                  }`}>
                    {entry.name.charAt(0)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 truncate">
                        {entry.name}
                      </span>
                      {isCurrentUser && (
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-bold rounded-full">
                          YOU
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      {entry.streakCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-500" />
                          {entry.streakCount} day streak
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Points */}
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-lg font-bold text-amber-600">
                      <Zap className="w-4 h-4" />
                      {entry.points}
                    </div>
                    <div className="text-xs text-slate-400">points</div>
                  </div>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      )}

      {/* Your Rank Card */}
      {user && !isLoading && leaderboard && (
        <FadeIn delay={0.5}>
          <AnimatedCard className="p-6 bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
                  {user.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Your Position</h3>
                  <p className="text-sm text-slate-500">Keep practicing to climb up!</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600">
                  #{leaderboard.find((e: any) => e.id === user.id)?.rank || '?'}
                </div>
                <div className="flex items-center gap-1 text-sm text-amber-600">
                  <Zap className="w-3 h-3" />
                  {user.points} points
                </div>
              </div>
            </div>
          </AnimatedCard>
        </FadeIn>
      )}
    </div>
  );
}
